import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Star,
  Sparkles,
  Building2,
  Handshake,
  TrendingUp,
  ArrowRight,
  Quote,
} from "lucide-react";

import { ourClientsTestimonials } from "@/data/testimonials";
import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import AnimatedCounter from "@/components/shared/AnimatedCounter";
import Eyebrow from "@/components/shared/Eyebrow";

const CLIENTS = [
  { name: "FinServ India",  industry: "NBFC & Lending",      highlight: "80% fraud loss reduction" },
  { name: "Tech Unicorn",   industry: "SaaS & Platforms",    highlight: "2,000+ hours saved" },
  { name: "RetailScale",    industry: "Commerce",            highlight: "15% margin lift" },
  { name: "InsureEdge",     industry: "Insurance",           highlight: "30% faster claims triage" },
  { name: "LogiCore",       industry: "Logistics",           highlight: "18% route efficiency gain" },
  { name: "HealthAxis",     industry: "Healthcare",          highlight: "Better patient risk scoring" },
];

const METRICS = [
  { value: "50+",  label: "Enterprise engagements" },
  { value: "99.9%",label: "Platform uptime delivered" },
  { value: "10+",  label: "Models in production" },
  { value: "24",   label: "Hour avg. response time" },
];

const PARTNERSHIP = [
  { title: "Discovery to Delivery", desc: "Strategy, pilots, and production in one continuous track." },
  { title: "Governed AI",           desc: "Compliance-ready data pipelines and audit trails." },
  { title: "Operational Uplift",    desc: "Automations that keep improving with feedback loops." },
  { title: "Outcome Metrics",       desc: "Clear KPIs with business impact tracking." },
];

const RESULTS = [
  "Fraud detection pipelines that cut investigation time by 60%.",
  "Automated audit workflows with improved coverage and accuracy.",
  "Real-time decision systems for faster approvals and lower risk.",
];

const OurClients = () => {
  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Our Clients"
        eyebrowIcon={Sparkles}
        title={
          <>
            Trusted by teams who ship{" "}
            <span className="gradient-text">AI with impact</span>
          </>
        }
        description="From regulated financial institutions to fast-moving product teams, we partner on strategy, data platforms, and production-grade AI deployments that deliver measurable business outcomes."
        actions={
          <>
            <Button asChild className="btn-primary rounded-full px-6 h-11">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Start a conversation <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
              <Link to="/case-studies">See case studies</Link>
            </Button>
          </>
        }
      />

      {/* Metrics */}
      <Section size="sm">
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-8 sm:p-10 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 relative z-10">
              {METRICS.map((m, i) => (
                <Reveal key={m.label} variant="up" delay={i * 0.06}>
                  <div className="text-center">
                    <AnimatedCounter value={m.value} label={m.label} />
                  </div>
                </Reveal>
              ))}
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* Client Highlights */}
      <Section>
        <SectionHeader
          eyebrow="Client highlights"
          eyebrowIcon={Building2}
          title={<>Outcomes across <span className="gradient-text">industries</span></>}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CLIENTS.map((c, i) => (
            <Reveal key={c.name} variant="up" delay={(i % 3) * 0.06}>
              <GlowCard className="p-6 h-full">
                <div className="flex items-start gap-3 mb-4">
                  <div className="feature-icon">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold">{c.name}</h3>
                    <p className="text-[var(--text-muted)] text-xs">{c.industry}</p>
                  </div>
                </div>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{c.highlight}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Partnership */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <Reveal variant="left">
            <GlowCard variant="liquid" className="aurora p-8 h-full overflow-hidden">
              <div className="relative z-10">
                <Eyebrow icon={Handshake} className="mb-4">Partnership</Eyebrow>
                <h3 className="font-display font-bold text-2xl mb-3">
                  Our <span className="gradient-text">partnership model</span>
                </h3>
                <p className="text-[var(--text-secondary)] text-sm mb-6 leading-relaxed">
                  We embed with your teams, align on measurable outcomes, and deliver systems that are secure,
                  explainable, and operationally reliable.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PARTNERSHIP.map((p, i) => (
                    <Reveal key={p.title} variant="up" delay={i * 0.06}>
                      <div className="rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-1)]/40 p-4">
                        <p className="text-sm font-semibold mb-1">{p.title}</p>
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{p.desc}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </GlowCard>
          </Reveal>

          <Reveal variant="right">
            <GlowCard className="p-8 h-full">
              <Eyebrow icon={TrendingUp} className="mb-4">Results</Eyebrow>
              <h3 className="font-display font-bold text-2xl mb-5">
                Results at a <span className="gradient-text">glance</span>
              </h3>
              <div className="space-y-4">
                {RESULTS.map((r, i) => (
                  <Reveal key={i} variant="up" delay={i * 0.07}>
                    <div className="flex items-start gap-3">
                      <Star className="w-5 h-5 text-[var(--brand-primary-light)] fill-[var(--brand-primary-light)] mt-0.5 flex-shrink-0" />
                      <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{r}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </GlowCard>
          </Reveal>
        </div>
      </Section>

      {/* Testimonials */}
      <Section>
        <SectionHeader
          eyebrow="Voices"
          eyebrowIcon={Quote}
          title={<>What our <span className="gradient-text">clients say</span></>}
        />
        <div className="grid md:grid-cols-2 gap-5">
          {ourClientsTestimonials.map((t, i) => (
            <Reveal key={t.author} variant="up" delay={(i % 2) * 0.08}>
              <GlowCard className="p-7 h-full">
                <Quote className="w-7 h-7 text-[var(--brand-primary-light)]/40 mb-3" />
                <p className="text-[var(--text-primary)] text-sm sm:text-base mb-5 leading-relaxed italic">
                  "{t.quote}"
                </p>
                <div>
                  <p className="text-sm font-semibold">{t.author}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{t.title}</p>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <Reveal variant="up">
          <GlowCard variant="liquid" className="aurora p-10 text-center max-w-4xl mx-auto">
            <Eyebrow icon={Sparkles} className="mb-4">Get started</Eyebrow>
            <h3 className="font-display font-bold text-3xl sm:text-4xl mb-3 leading-tight">
              Ready to build your <span className="gradient-text">success story?</span>
            </h3>
            <p className="text-[var(--text-secondary)] mb-7 max-w-xl mx-auto">
              Tell us about your goals and we will propose a clear, measurable AI roadmap tailored to your business.
            </p>
            <Button asChild className="btn-primary rounded-full px-8 h-12">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Talk to us <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </GlowCard>
        </Reveal>
      </Section>
    </div>
  );
};

export default OurClients;
